import { VFC } from "react";
import { useToggle } from "react-use";
import { NodeInstance } from "src/types/node.types";
import { EditorState } from "../types/editor-state.types";
import { EditorAction } from "../types/editor-action.types";
import { CreateNodeDialog } from "./create-node-dialog";
import { MagicWandIcon, PlusIcon, TrashIcon } from "@radix-ui/react-icons";
import { useHotkeys } from "react-hotkeys-hook";
import { css } from "src/styles/theme";
import { FAB } from "src/components/fab";
import { AlertDialog } from "src/components/alert-dialog";

export interface EditorToolbarProps {
  state: EditorState;
  dispatch: (action: EditorAction) => void;
}

export const EditorToolbar: VFC<EditorToolbarProps> = (props) => {
  const [isCreateModalOpen, toggleCreateModal] = useToggle(false);
  const [isDeleteConfirmationOpen, toggleDeleteConfirmation] = useToggle(false);
  const isDisabled = isCreateModalOpen || isDeleteConfirmationOpen;
  const hasNodes = Object.keys(props.state.nodes).length > 0;

  const showCreateModal = () => {
    !isCreateModalOpen && toggleCreateModal();
  };

  const addNodesHandler = (nodes: NodeInstance[]) => {
    toggleCreateModal();

    nodes.forEach((node) => {
      props.dispatch({ type: "ADD_NODE", node });
    });
  };

  const cleanUpPositions = () => props.dispatch({ type: "CLEAN_UP_POSITIONS" });

  const deleteAll = () => {
    toggleDeleteConfirmation();
  };

  const deleteAllConfirmed = () => {
    props.dispatch({ type: "DELETE_ALL" });
  };

  useHotkeys("ctrl+enter", showCreateModal, { enabled: !isDisabled });
  useHotkeys("alt+f", cleanUpPositions, { enabled: !isDisabled && hasNodes });
  useHotkeys("alt+shift+d", deleteAll, { enabled: !isDisabled && hasNodes });

  return (
    <>
      <div className={$toolbar()}>
        {hasNodes && (
          <>
            <FAB
              onClick={deleteAll}
              label="Delete all nodes"
              info="alt+shift+d"
              color="danger"
            >
              <TrashIcon />
            </FAB>
            <FAB onClick={cleanUpPositions} label="Format nodes" info="alt+f">
              <MagicWandIcon />
            </FAB>
          </>
        )}
        <FAB
          look="primary"
          onClick={showCreateModal}
          label="Add new node"
          info="ctrl+enter"
        >
          <PlusIcon />
        </FAB>
      </div>

      <AlertDialog.Root
        open={isDeleteConfirmationOpen}
        onOpenChange={toggleDeleteConfirmation}
      >
        <AlertDialog.Portal>
          <AlertDialog.Overlay onClick={toggleDeleteConfirmation} />
          <AlertDialog.Content>
            <AlertDialog.Title>Delete all nodes?</AlertDialog.Title>
            <AlertDialog.Description>
              Are you sure you want to delete all nodes? This action cannot be
              undone.
            </AlertDialog.Description>
            <AlertDialog.Actions>
              <AlertDialog.Cancel color="neutral">Cancel</AlertDialog.Cancel>
              <AlertDialog.Action onClick={deleteAllConfirmed} color="danger">
                Delete all nodes
              </AlertDialog.Action>
            </AlertDialog.Actions>
          </AlertDialog.Content>
        </AlertDialog.Portal>
      </AlertDialog.Root>

      {isCreateModalOpen && (
        <CreateNodeDialog
          onClose={toggleCreateModal}
          onCreate={addNodesHandler}
        />
      )}
    </>
  );
};

const $toolbar = css({
  display: "grid",
  gap: "$4",
  justifyItems: "center",
  alignItems: "center",
});
